// SPDX-License-Identifier: AGPL-3.0-or-later

import {showDmActionErrorModal} from '@app/features/app/components/alerts/DmActionErrorModal';
import styles from '@app/features/app/components/dialogs/shared/RecipientList.module.css';
import {GroupDMAvatar} from '@app/features/app/components/shared/GroupDMAvatar';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import {
	DIRECT_MESSAGE_DESCRIPTOR,
	GROUP_DM_DESCRIPTOR,
	SEARCH_FRIENDS_DESCRIPTOR,
	SENT_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Relationships from '@app/features/relationship/state/Relationships';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Scroller} from '@app/features/ui/components/Scroller';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import {blockIfAccountLimited} from '@app/features/user/utils/AccountLimitUtils';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {RelationshipTypes} from '@fluxer/constants/src/UserConstants';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {MagnifyingGlassIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useId, useMemo, useRef, useState} from 'react';

export interface RecipientItem {
	id: string;
	user: User;
	type: 'dm' | 'group_dm' | 'friend';
	channelId?: string;
	channelName?: string;
}

export const useRecipientItems = () => {
	const {i18n} = useLingui();
	const relationships = Relationships.getRelationships();
	const dmChannels = Channels.dmChannels;
	const usersSnapshot = Users.usersList;
	const initialOrderRef = useRef<Array<string> | null>(null);
	return useMemo(() => {
		const recipients: Array<RecipientItem> = [];
		const friends = relationships.filter((r) => r.type === RelationshipTypes.FRIEND);
		const friendIds = new Set(friends.map((f) => f.id));
		dmChannels.forEach((channel) => {
			if (channel.type === ChannelTypes.DM && channel.recipientIds.length > 0) {
				const recipientId = channel.recipientIds[0];
				const user = Users.getUser(recipientId);
				if (user && friendIds.has(recipientId)) {
					recipients.push({
						id: recipientId,
						user,
						type: 'dm',
						channelId: channel.id,
						channelName: channel.name,
					});
					friendIds.delete(recipientId);
				}
			}
		});
		dmChannels.forEach((channel) => {
			if (channel.type === ChannelTypes.GROUP_DM) {
				const recipientId = channel.recipientIds[0];
				const user = Users.getUser(recipientId);
				if (user) {
					recipients.push({
						id: channel.id,
						user,
						type: 'group_dm',
						channelId: channel.id,
						channelName: ChannelUtils.getDMDisplayName(channel),
					});
				}
			}
		});
		friendIds.forEach((userId) => {
			const user = Users.getUser(userId);
			if (user) {
				recipients.push({
					id: userId,
					user,
					type: 'friend',
				});
			}
		});
		if (initialOrderRef.current === null) {
			initialOrderRef.current = recipients.map((r) => r.id);
			return recipients;
		}
		const orderMap = new Map(initialOrderRef.current.map((id, index) => [id, index]));
		const existingIds = new Set(initialOrderRef.current);
		const sorted = recipients.sort((a, b) => {
			const aInOrder = existingIds.has(a.id);
			const bInOrder = existingIds.has(b.id);
			if (aInOrder && bInOrder) {
				return (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0);
			}
			if (aInOrder) return -1;
			if (bInOrder) return 1;
			return 0;
		});
		return sorted;
	}, [relationships, dmChannels, usersSnapshot, i18n.locale]);
};

interface RecipientListProps {
	recipients: Array<RecipientItem>;
	sendingTo: Set<string>;
	sentTo: Map<string, boolean>;
	onSend: (item: RecipientItem) => void;
	defaultButtonLabel: React.ReactNode;
	sentButtonLabel?: React.ReactNode;
	buttonClassName?: string;
	searchPlaceholder?: string;
	scrollerKey?: string;
	searchQuery?: string;
	onSearchQueryChange?: (value: string) => void;
	showSearchInput?: boolean;
	emptyText?: React.ReactNode;
}

export const RecipientList = observer((props: RecipientListProps) => {
	const {i18n} = useLingui();
	const listId = useId();
	const [internalSearchQuery, setInternalSearchQuery] = useState('');
	const searchQuery = props.searchQuery ?? internalSearchQuery;
	const filteredRecipients = useMemo(() => {
		if (!searchQuery.trim()) {
			return props.recipients;
		}
		const query = searchQuery.toLowerCase();
		return props.recipients.filter((item) => {
			const username = item.user.username.toLowerCase();
			const displayName = NicknameUtils.getNickname(item.user, null).toLowerCase();
			const channelName = (item.channelName || '').toLowerCase();
			return username.includes(query) || displayName.includes(query) || channelName.includes(query);
		});
	}, [searchQuery, props.recipients]);
	const emptyText = props.emptyText ?? (
		<Trans comment="Shown in a list of people to choose from when the account has no friends and no direct messages yet.">
			No friends or direct messages yet.
		</Trans>
	);
	const handleSearchChange = (value: string) => {
		if (props.onSearchQueryChange) {
			props.onSearchQueryChange(value);
		} else {
			setInternalSearchQuery(value);
		}
	};
	return (
		<div className={styles.content} data-flx="app.recipient-list.content">
			{(props.showSearchInput ?? true) && (
				<Input
					value={searchQuery}
					onChange={(e) => handleSearchChange(e.target.value)}
					placeholder={props.searchPlaceholder ?? i18n._(SEARCH_FRIENDS_DESCRIPTOR)}
					leftIcon={
						<MagnifyingGlassIcon
							size={20}
							weight="bold"
							className={styles.searchIcon}
							data-flx="app.recipient-list.search-icon"
						/>
					}
					className={styles.searchInput}
					data-flx="app.recipient-list.search-input.search-change"
				/>
			)}
			<div className={styles.listContainer} data-flx="app.recipient-list.list-container">
				<Scroller
					className={styles.scroller}
					key={props.scrollerKey ?? 'recipient-list-scroller'}
					fade={false}
					data-flx="app.recipient-list.scroller"
				>
					{filteredRecipients.length === 0 ? (
						<div className={styles.noResults} data-flx="app.recipient-list.no-results">
							{searchQuery.trim() ? <Trans>No friends match that.</Trans> : emptyText}
						</div>
					) : (
						<div className={styles.friendList} data-flx="app.recipient-list.friend-list">
							{filteredRecipients.map((item) => {
								const userId = item.type === 'group_dm' ? item.id : item.user.id;
								const isSending = props.sendingTo.has(userId);
								const isSent = props.sentTo.has(userId);
								const displayName =
									item.type === 'group_dm' ? item.channelName : NicknameUtils.getNickname(item.user, null);
								const secondaryText = (() => {
									if (item.type === 'dm') return i18n._(DIRECT_MESSAGE_DESCRIPTOR);
									if (item.type === 'group_dm') return i18n._(GROUP_DM_DESCRIPTOR);
									return item.user.username;
								})();
								const groupChannel =
									item.type === 'group_dm' && item.channelId ? Channels.getChannel(item.channelId) : null;
								const itemId = `${listId}-${userId}`;
								return (
									<div key={userId} className={styles.friendItem} data-flx="app.recipient-list.friend-item">
										<div className={styles.friendItemLeft} data-flx="app.recipient-list.friend-item-left">
											{groupChannel ? (
												<GroupDMAvatar channel={groupChannel} size={32} data-flx="app.recipient-list.group-dm-avatar" />
											) : (
												<StatusAwareAvatar
													user={item.user}
													size={32}
													data-flx="app.recipient-list.status-aware-avatar"
												/>
											)}
											<div className={styles.friendInfo} data-flx="app.recipient-list.friend-info">
												<span
													id={`${itemId}-name`}
													className={styles.friendName}
													data-flx="app.recipient-list.friend-name"
												>
													{displayName}
												</span>
												<span
													id={`${itemId}-secondary`}
													className={styles.friendSecondary}
													data-flx="app.recipient-list.friend-secondary"
												>
													{secondaryText}
												</span>
											</div>
										</div>
										<Button
											id={`${itemId}-action`}
											aria-labelledby={`${itemId}-action ${itemId}-name`}
											aria-describedby={`${itemId}-secondary`}
											small
											variant="secondary"
											onClick={() => props.onSend(item)}
											disabled={isSent}
											submitting={isSending}
											className={props.buttonClassName ?? styles.actionButton}
											data-flx="app.recipient-list.action-button.send"
										>
											{isSent ? (props.sentButtonLabel ?? i18n._(SENT_DESCRIPTOR)) : props.defaultButtonLabel}
										</Button>
									</div>
								);
							})}
						</div>
					)}
				</Scroller>
			</div>
		</div>
	);
});

const recipientSendLogger = new Logger('RecipientList');
export function useSendLinkToRecipients(getContent: () => string | null, onError?: (error: unknown) => void) {
	const [sendingTo, setSendingTo] = useState(new Set<string>());
	const [sentTo, setSentTo] = useState(new Map<string, boolean>());
	const send = useCallback(
		async (item: RecipientItem) => {
			const content = getContent();
			if (content === null) return;
			if (blockIfAccountLimited()) return;
			const userId = item.type === 'group_dm' ? item.id : item.user.id;
			setSendingTo((previous) => new Set(previous).add(userId));
			try {
				const targetChannelId = item.channelId ?? (await PrivateChannelCommands.ensureDMChannel(item.user.id));
				const result = await MessageCommands.send(targetChannelId, {
					content,
					nonce: SnowflakeUtils.fromTimestamp(Date.now()),
				});
				if (result) setSentTo((previous) => new Map(previous).set(userId, true));
			} catch (error) {
				recipientSendLogger.error('Failed to send shared link:', error);
				(onError ?? showDmActionErrorModal)(error);
			} finally {
				setSendingTo((previous) => {
					const next = new Set(previous);
					next.delete(userId);
					return next;
				});
			}
		},
		[getContent, onError],
	);
	return {sendingTo, sentTo, send};
}
